import {
  FC,
  JSX,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  incrementHouseholdsCurrentIndex,
  removeHouseholdConfirmed,
  removeHouseholdTransferred,
  setHouseholdsCurrentIndex,
  setOpenHouseholdComposition,
  setUnconfirmedHouseholds,
  updateHouseholdMemberInCertifications,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import ContactPropertyToRemoveMemberDialog from './ContactPropertyToRemoveMemberDialog';
import { RootState } from 'src/redux/rootReducerType';
import {
  Certification,
  CertificationProperty,
  CertHouseholdData,
  CertMember,
} from 'src/types/Certification';
import { fadeStyle, HOUSEHOLD_STRUCTURE_STEPS } from '../constants';
import HouseholdCompositionMembersList from './HouseholdCompositionMembersList';
import AddHouseholdMemberScreen from '../Household/AddHouseholdMemberScreen';
import EditHouseholdMemberScreen from '../Household/EditHouseholdMemberScreen';
import TransferSuccessView from 'src/components/NewRenterInterface/Household/TransferSuccessView';
import HouseholdConfirmSuccessView from './HouseholdConfirmSuccessView';
import HouseholdConfirmContinueView from './HouseholdConfirmContinueView';
import { getMemberFullName } from 'src/components/Compliance/Certification/Household/HouseholdUtils';

export interface HouseholdCompositionPageRef {
  currentStep: HOUSEHOLD_STRUCTURE_STEPS;
  goBackToList: () => void;
}

interface HouseholdCompositionPageProps {
  property?: CertificationProperty;
  ref?: Ref<HouseholdCompositionPageRef>;
  onStepChange?: (step: HOUSEHOLD_STRUCTURE_STEPS) => void;
}

/**
 * Page component for household composition management with state machine.
 * Can be used standalone with property prop or as part of modal flow.
 * WebSocket-driven state updates are handled by CertificationUpdateManager.
 */
const HouseholdCompositionPage: FC<HouseholdCompositionPageProps> = ({
  property,
  ref,
  onStepChange,
}) => {
  const dispatch = useDispatch();
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];
  const householdsTransferred = useSelector(
    (state: RootState) => state.newRenterInterface.householdsTransferred
  );
  const householdsConfirmed = useSelector(
    (state: RootState) => state.newRenterInterface.householdsConfirmed
  );
  const unconfirmedHouseholds = useSelector(
    (state: RootState) => state.newRenterInterface.unconfirmedHouseholds
  );
  const [currentStep, setCurrentStep] = useState<HOUSEHOLD_STRUCTURE_STEPS>(
    HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLDS_LIST
  );
  const [currentHousehold, setCurrentHousehold] =
    useState<CertHouseholdData | null>(null);
  const [currentProperty, setCurrentProperty] = useState<
    CertificationProperty | undefined
  >(undefined);
  const [memberToEditId, setMemberToEditId] = useState<number | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<CertMember | null>(null);
  const [showContinueBridge, setShowContinueBridge] = useState(false);

  // Only managing cert members (HoH, Co-Head, Spouse, Helper) may add/edit
  // members. Non-managing members see a read-only list (no Add button, cards
  // not tappable).
  const currentUserId = user?.id ?? null;
  const isManaging = useMemo(
    () =>
      currentHousehold?.cert_members?.some(
        (m) => m.user_id === currentUserId && m.is_managing
      ) ?? false,
    [currentHousehold, currentUserId]
  );

  const currentTransferredHousehold = householdsTransferred[0] ?? null;

  useEffect(() => {
    if (property) {
      const cert = certifications?.find(
        (c: Certification) => c.property?.id === property.id
      );

      if (cert?.cert_household) {
        setCurrentHousehold(cert.cert_household);
        setCurrentProperty(property);
        if (cert.cert_household.id !== currentHousehold?.id) {
          setCurrentStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
        }
      }
    }
  }, [currentHousehold?.id, property, certifications]);

  // The member being edited is derived from the Redux-backed household, so
  // WS-driven updates (e.g. contact_info_updated) are reflected live without
  // holding a stale local snapshot.
  const memberToEdit = useMemo<CertMember | null>(
    () =>
      currentHousehold?.cert_members?.find(
        (m) => m.cert_member_id === memberToEditId
      ) ?? null,
    [currentHousehold, memberToEditId]
  );

  // If the member being edited disappears (e.g. a WS update removes them in
  // another tab), fall back to the member list instead of an empty edit step.
  useEffect(() => {
    if (
      currentStep !== HOUSEHOLD_STRUCTURE_STEPS.EDIT_MEMBER ||
      memberToEditId == null ||
      !currentHousehold?.cert_members
    ) {
      return;
    }
    const stillExists = currentHousehold.cert_members.some(
      (m) => m.cert_member_id === memberToEditId
    );
    if (!stillExists) {
      setMemberToEditId(null);
      setCurrentStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
    }
  }, [currentStep, memberToEditId, currentHousehold]);

  const onHouseholdTransferredComplete = useCallback((): void => {
    if (!currentTransferredHousehold) {
      return;
    }
    const { certification_id } = currentTransferredHousehold;
    const isLastTransfer = householdsTransferred.length === 1;
    dispatch(removeHouseholdTransferred(certification_id));
    dispatch(incrementHouseholdsCurrentIndex());
    if (user?.id) {
      dispatch(setUnconfirmedHouseholds(user.id));
    }
    if (
      isLastTransfer &&
      unconfirmedHouseholds.length === 0 &&
      householdsConfirmed.length === 0
    ) {
      dispatch(setOpenHouseholdComposition(false));
    }
  }, [
    currentTransferredHousehold,
    dispatch,
    user?.id,
    householdsTransferred,
    unconfirmedHouseholds,
    householdsConfirmed,
  ]);

  const handleConfirmSuccessNext = useCallback((): void => {
    const certId = householdsConfirmed[0];
    if (certId) {
      const isLastConfirmation = householdsConfirmed.length === 1;
      dispatch(removeHouseholdConfirmed(certId));
      dispatch(setHouseholdsCurrentIndex(0));
      if (
        isLastConfirmation &&
        unconfirmedHouseholds.length === 0 &&
        householdsTransferred.length === 0
      ) {
        dispatch(setOpenHouseholdComposition(false));
      }
    }
  }, [
    dispatch,
    householdsConfirmed,
    unconfirmedHouseholds,
    householdsTransferred,
  ]);

  const handleAddMemberClick = (): void => {
    setCurrentStep(HOUSEHOLD_STRUCTURE_STEPS.ADD_MEMBER);
  };

  const handleMemberAdded = (): void => {
    setCurrentStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
  };

  const handleCancelAddMember = (): void => {
    setCurrentStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
  };

  const handleEditMember = (member: CertMember): void => {
    setMemberToEditId(member.cert_member_id);
    setCurrentStep(HOUSEHOLD_STRUCTURE_STEPS.EDIT_MEMBER);
  };

  const handleMemberUpdated = (updatedMembers: CertMember[]): void => {
    const certificationId = certifications.find(
      (c) => c.cert_household?.id === currentHousehold?.id
    )?.id;
    if (certificationId != null) {
      updatedMembers.forEach((updatedMember) => {
        dispatch(
          updateHouseholdMemberInCertifications({
            certificationId,
            householdMember: updatedMember,
            action: 'update',
          })
        );
      });
    }
    setMemberToEditId(null);
    setCurrentStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
  };

  const handleCancelEditMember = (): void => {
    setMemberToEditId(null);
    setCurrentStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
  };

  const handleDeleteMember = useCallback((member: CertMember): void => {
    setMemberToDelete(member);
  }, []);

  const handleCancelDelete = useCallback((): void => {
    setMemberToDelete(null);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      currentStep,
      goBackToList: () => {
        setMemberToEditId(null);
        setCurrentStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
      },
    }),
    [currentStep]
  );

  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  const renderStepContent = (): JSX.Element | null => {
    switch (currentStep) {
      case HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY:
        if (!currentHousehold) {
          return null;
        }
        return (
          <HouseholdCompositionMembersList
            household={currentHousehold}
            canManage={isManaging}
            currentUserId={currentUserId ?? undefined}
            onAddMember={handleAddMemberClick}
            onEditMember={isManaging ? handleEditMember : undefined}
            onDeleteMember={handleDeleteMember}
          />
        );

      case HOUSEHOLD_STRUCTURE_STEPS.ADD_MEMBER:
        if (!currentHousehold) {
          return null;
        }
        return (
          <AddHouseholdMemberScreen
            household={currentHousehold}
            property={currentProperty}
            certificationId={
              certifications.find(
                (c) => c.cert_household?.id === currentHousehold.id
              )?.id
            }
            onSubmit={handleMemberAdded}
            onCancel={handleCancelAddMember}
          />
        );

      case HOUSEHOLD_STRUCTURE_STEPS.EDIT_MEMBER:
        if (!currentHousehold || !memberToEdit) {
          return null;
        }
        return (
          <EditHouseholdMemberScreen
            household={currentHousehold}
            member={memberToEdit}
            property={currentProperty}
            onSubmit={handleMemberUpdated}
            onCancel={handleCancelEditMember}
            onDelete={handleDeleteMember}
          />
        );

      case HOUSEHOLD_STRUCTURE_STEPS.MEMBER_DETAILS:
        return <Box sx={fadeStyle}>TODO: Not implemented yet</Box>;

      default:
        return null;
    }
  };

  if (householdsConfirmed.length > 0) {
    // unconfirmedHouseholds has already had the confirmed one removed (Redux
    // recomputes it in setHouseholdCompositionConfirmed), so any remaining
    // entries are genuinely next in queue.
    const hasMore = unconfirmedHouseholds.length > 0;

    if (showContinueBridge) {
      return (
        <HouseholdConfirmContinueView
          propertyName={unconfirmedHouseholds[0]?.property?.name ?? null}
          onNext={() => {
            handleConfirmSuccessNext();
            setShowContinueBridge(false);
          }}
        />
      );
    }

    return (
      <HouseholdConfirmSuccessView
        onNext={() => {
          if (hasMore) {
            setShowContinueBridge(true);
          } else {
            handleConfirmSuccessNext();
          }
        }}
      />
    );
  }

  if (householdsTransferred.length > 0) {
    return (
      <TransferSuccessView
        newHohName={
          getMemberFullName(currentTransferredHousehold?.new_head) ??
          'New Head of Household'
        }
        demotedMemberName={
          getMemberFullName(currentTransferredHousehold?.demoted_member) ??
          'Previous Head of Household'
        }
        onComplete={onHouseholdTransferredComplete}
      />
    );
  }

  const memberFullName = getMemberFullName(memberToDelete) ?? 'this member';
  const contactEmail =
    currentProperty?.property_email ||
    `${currentProperty?.slug}@prontohousing.io`;

  return (
    <>
      {renderStepContent()}
      <ContactPropertyToRemoveMemberDialog
        open={!!memberToDelete}
        onClose={handleCancelDelete}
        memberName={memberFullName}
        propertyEmail={contactEmail}
      />
    </>
  );
};

export default HouseholdCompositionPage;
